import { api } from "./client";
import type { Content, User, AuditEntry, Tile, Section, Department, Meta } from "../types";

// ---- Auth ----
export const authApi = {
  me: () => api<{ user: User | null }>("/api/auth/me"),
  login: (username: string, password: string) =>
    api<{ user: User }>("/api/auth/login", { method: "POST", json: { username, password } }),
  logout: () => api<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  changePassword: (current_password: string, new_password: string) =>
    api<{ ok: true }>("/api/auth/change-password", {
      method: "POST",
      json: { current_password, new_password },
    }),
};

// ---- Public content ----
export const contentApi = {
  get: () => api<Content>("/api/content"),
};

// ---- Admin: departments ----
export const departmentsApi = {
  list: () => api<{ departments: Department[] }>("/api/manage/departments"),
  create: (d: Partial<Department>) => api<{ id: string }>("/api/manage/departments", { method: "POST", json: d }),
  update: (id: string, d: Partial<Department>) =>
    api<{ ok: true }>(`/api/manage/departments/${id}`, { method: "PATCH", json: d }),
  remove: (id: string) => api<void>(`/api/manage/departments/${id}`, { method: "DELETE" }),
};

// ---- Admin: sections ----
export const sectionsApi = {
  list: (departmentId?: string) =>
    api<{ sections: Section[] }>(
      `/api/manage/sections${departmentId ? `?department_id=${departmentId}` : ""}`,
    ),
  create: (s: Partial<Section> & { department_id: string }) =>
    api<{ id: string }>("/api/manage/sections", { method: "POST", json: s }),
  update: (id: string, s: Partial<Section>) =>
    api<{ ok: true }>(`/api/manage/sections/${id}`, { method: "PATCH", json: s }),
  remove: (id: string) => api<void>(`/api/manage/sections/${id}`, { method: "DELETE" }),
};

// ---- Admin: tiles ----
export const tilesApi = {
  list: (sectionId?: string) =>
    api<{ tiles: Tile[] }>(`/api/manage/tiles${sectionId ? `?section_id=${sectionId}` : ""}`),
  create: (t: Partial<Tile> & { section_id: string }) =>
    api<{ id: string }>("/api/manage/tiles", { method: "POST", json: t }),
  update: (id: string, t: Partial<Tile>) =>
    api<{ ok: true }>(`/api/manage/tiles/${id}`, { method: "PATCH", json: t }),
  remove: (id: string) => api<void>(`/api/manage/tiles/${id}`, { method: "DELETE" }),
};

// ---- Admin: users ----
export const usersApi = {
  list: () => api<{ users: User[] }>("/api/manage/users"),
  create: (u: { username: string; password: string; role: "admin" | "editor"; display_name?: string; email?: string }) =>
    api<{ username: string }>("/api/manage/users", { method: "POST", json: u }),
  update: (username: string, u: Partial<User>) =>
    api<{ ok: true }>(`/api/manage/users/${username}`, { method: "PATCH", json: u }),
  remove: (username: string) => api<void>(`/api/manage/users/${username}`, { method: "DELETE" }),
  resetPassword: (username: string, new_password: string) =>
    api<{ ok: true }>(`/api/manage/users/${username}/reset-password`, {
      method: "POST",
      json: { new_password },
    }),
};

// ---- Admin: reorder ----
export const reorderApi = {
  apply: (params: { type: "department" | "section" | "tile"; parent_id?: string; ordered_ids: string[] }) =>
    api<{ ok: true }>("/api/manage/reorder", { method: "POST", json: params }),
};

// ---- Admin: settings ----
export const settingsApi = {
  get: () => api<Meta>("/api/manage/settings"),
  update: (s: Partial<Meta>) => api<{ ok: true }>("/api/manage/settings", { method: "PATCH", json: s }),
};

// ---- Admin: audit ----
export const auditApi = {
  list: (limit = 100, offset = 0) =>
    api<{ entries: AuditEntry[] }>(`/api/manage/audit?limit=${limit}&offset=${offset}`),
};

// ---- Admin: uploads ----
export const uploadsApi = {
  requestSas: (filename: string, content_type: string, purpose: "banner" | "icon" = "banner") =>
    api<{ upload_url: string; public_url: string; blob_name: string; expires_on: string }>(
      "/api/manage/upload",
      { method: "POST", json: { filename, content_type, purpose } },
    ),
  async uploadFile(file: File, purpose: "banner" | "icon" = "banner"): Promise<string> {
    const sas = await this.requestSas(file.name, file.type, purpose);
    const res = await fetch(sas.upload_url, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": file.type,
      },
      body: file,
    });
    if (!res.ok) throw new Error(`upload failed (${res.status})`);
    return sas.public_url;
  },
};
